import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { LoadingService } from '../../core/services/loading.service';
import { ItineraryTemplatesService } from '../../core/services/itinerary-templates.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DefaultPageSize } from '../../core/dto/defaultPage.size';
import { Pagination } from '../../core/dto/pagination';
import { LanguageService } from '../../core/services/language.service';
import { SettingService } from '../../core/services/setting.service';
import { TranslocoService } from '@ngneat/transloco';
import { AppUtils } from '../../core/config/app.utils';
import { SETTINGS } from '../../core/config/common.settings';

interface TemplateCreator {
  name: string;
  createdAt: any;
}

@Component({
  selector: 'app-itinerary-templates',
  templateUrl: './itinerary-templates.component.html',
  styleUrls: ['./itinerary-templates.component.scss'],
})
export class ItineraryTemplatesComponent implements OnInit {

  constructor(
    private loading: LoadingService,
    private itineraryTemplatesService: ItineraryTemplatesService,
    private messageService: NzMessageService,
    private modal: NzModalService,
    private languageService: LanguageService,
    private settingService: SettingService,
    private translocoService: TranslocoService,
    private router: Router,
  ) {}

  itineraryTemplates: any[] = [];
  pageSize = new DefaultPageSize();
  sortField = '';
  sortOrder: any;
  itinerarySearchName = '';
  languages: any[] = [];

  sortColumn = {
    compareName: (a: TemplateCreator, b: TemplateCreator) =>
      a?.name?.localeCompare(b.name),
    compareDate: (a: TemplateCreator, b: TemplateCreator) => {
      return (new Date(b.createdAt) as any) - (new Date(a.createdAt) as any);
    },
  };

  ngOnInit(): void {
    this.getLanguages().then();
    this.search();
  }

  onTableParamsChange(params: NzTableQueryParams): void {
    this.loading.start();
    this.pageSize.pageSize = params.pageSize;
    const currentSort = params.sort.find(item => item.value !== null);
    this.pageSize.pageIndex = params.pageIndex;
    this.sortOrder =
      (currentSort && AppUtils.getSortOrder(currentSort.value)) || null;
    const keyword = this.itinerarySearchName;
    const payload = Object.assign({}, new Pagination(this.pageSize), {
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      keyword,
    });
    this.itineraryTemplatesService.getPagedItineraries(payload).then((object: any) => {
      this.itineraryTemplates = object.data;
      this.pageSize.length = object.metadata[0]?.total;
      this.loading.stop();
    });
  }

  search(): void {
    this.loading.start();
    const keyword = this.itinerarySearchName;
    this.pageSize.pageIndex = 1;
    const payload = Object.assign({}, new Pagination(this.pageSize), {
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      keyword,
    });
    this.itineraryTemplatesService.getPagedItineraries(payload).then((object: any) => {
      this.itineraryTemplates = object.data;
      this.pageSize.length = object.metadata[0]?.total;
      this.loading.stop();
    });
  }

  reset(): void {
    this.itinerarySearchName = '';
    this.search();
  }

  sortByName(): void {
    this.sortField = 'full_name';
  }

  sortByDate(): void {
    this.sortField = 'createdAt';
  }

  sortByCreator(): void {
    this.sortField = 'creator.name';
  }

  sortByCategory(): void {
    this.sortField = 'tour.category';
  }

  sortByLanguage(): void {
    this.sortField = 'tour.language';
  }

  sortByCurrency(): void {
    this.sortField = 'tour.currency';
  }

  sortByUpdatedAt(): void {
    this.sortField = 'updatedAt';
  }

  languageByCode(languageCode: any): string {
    const languageName = this.languages.find(
      (element: any) => element.code === languageCode
    )?.name;
    if (!languageName) {
      return this.getEmptyValue();
    }
    return languageName;
  }

  async getLanguages(): Promise<void> {
    this.languages = await this.languageService.getContentLanguages();
  }

  getEmptyValue(): string {
    return this.translocoService.translate('itineraries.common.emptyValue');
  }

  getDurationLabel(days: number): string {
    const nights = days > 0 ? days - 1 : 0;
    return `${days} ${this.translocoService.translate('itineraryTemplates.table.days')} ${this.translocoService.translate('itineraryTemplates.table.and')} ${nights} ${this.translocoService.translate('itineraryTemplates.table.nights')}`;
  }

  onEdit(data: any): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/admin/template-itinerary/edit', data._id]));
    window.open(url, '_blank');
  }

  onPreview(data: any): void {
    const theme = SETTINGS.THEME_TYPES_NAVI_B2C_TEMPLATE.find((t: any) => t.value === data.tour.themeType);
    const params = new URLSearchParams({ rretMgxg: 'ffliwqadrjt' });
    const baseUrl = this.settingService.getHostnameUrl();
    window.open(`${baseUrl}${theme?.url}/${data._id}/false?${params.toString()}`, '_blank');
  }

  onUseTemplate(data: any): void {
    this.router.navigate(['/admin/itineraries/template/add'], {
      queryParams: { templateId: data._id },
    });
  }

  showDeleteConfirm(id: string): void {
    this.modal.confirm({
      nzTitle: this.translocoService.translate('itineraryTemplates.modals.delete.title'),
      nzContent: this.translocoService.translate('itineraryTemplates.modals.delete.content'),
      nzOkText: this.translocoService.translate('itineraryTemplates.modals.delete.ok'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => this.deleteTemplate(id),
      nzCancelText: this.translocoService.translate('itineraryTemplates.modals.common.cancel'),
    });
  }

  deleteTemplate(id: string): void {
    this.loading.start();
    this.itineraryTemplatesService.deleteItinerary(id).then(() => {
      this.messageService.success(this.translocoService.translate('itineraryTemplates.messages.deleted'));
      this.search();
    }).catch(() => {
      this.loading.stop();
    });
  }

  countrySequence(days: Array<{ destination?: { country?: string | null } }> | null | undefined): string {
    if (!days) return '-';

    const countries = days
      .map(day => day.destination?.country)
      .filter((country): country is string => !!country && country.trim().length > 0);

    const unique = [...new Set(countries)];
    return unique.length ? unique.join(', ') : '-';
  }
}
